import React, { useState } from 'react';
import { X, Copy, Check, Download, Tag, Calendar, Hash } from 'lucide-react';

/**
 * Modal for viewing contract details with syntax-highlighted JSON
 */
const ContractViewModal = ({ contract, isOpen, onClose, onExport, addToast }) => {
    const [copied, setCopied] = useState(false);

    if (!isOpen || !contract) return null;

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(
                JSON.stringify(contract.schema_content, null, 2)
            );
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
            addToast?.('Schema copied to clipboard', 'success');
        } catch (error) {
            addToast?.('Failed to copy', 'error');
        }
    };

    // Simple JSON syntax highlighting - Optimized for Dark Theme
    const highlightJSON = (json) => {
        const str = JSON.stringify(json, null, 2);
        return str
            .replace(/("[\w_-]+"):/g, '<span class="text-purple-400">$1</span>:')
            .replace(/: (".*?")/g, ': <span class="text-green-400">$1</span>')
            .replace(/: (\d+)/g, ': <span class="text-blue-400">$1</span>')
            .replace(/: (true|false|null)/g, ': <span class="text-orange-400">$1</span>');
    };

    const tags = contract.tags || [];

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="relative bg-[#252526] rounded-2xl shadow-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col border border-[#3c3c3c]">
                {/* Header */}
                <div className="flex-shrink-0 px-6 py-4 border-b border-[#3c3c3c]">
                    <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0 flex-1">
                            <h2 className="text-xl font-semibold text-white truncate">
                                {contract.name}
                            </h2>
                            {contract.description && (
                                <p className="text-sm text-[#808080] mt-1">
                                    {contract.description}
                                </p>
                            )}
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-[#3c3c3c] rounded-lg transition-colors flex-shrink-0"
                        >
                            <X size={20} className="text-[#808080]" />
                        </button>
                    </div>

                    {/* Metadata Row */}
                    <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-[#808080]">
                        <span className="flex items-center gap-1.5">
                            <Hash size={14} />
                            ID: {contract.id}
                        </span>
                        <span className="flex items-center gap-1.5">
                            <Calendar size={14} />
                            Created: {new Date(contract.created_at).toLocaleDateString()}
                        </span>
                        <span className="flex items-center gap-1.5">
                            <Calendar size={14} />
                            Updated: {new Date(contract.updated_at).toLocaleDateString()}
                        </span>
                    </div>

                    {/* Tags */}
                    {tags.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-3">
                            {tags.map((tag, idx) => (
                                <span
                                    key={idx}
                                    className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-900/30 text-blue-400"
                                >
                                    <Tag size={10} />
                                    {tag}
                                </span>
                            ))}
                        </div>
                    )}
                </div>

                {/* Schema Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    <div className="bg-[#1e1e1e] rounded-xl border border-[#3c3c3c] overflow-hidden">
                        <div className="flex items-center justify-between px-4 py-2 bg-[#252526] border-b border-[#3c3c3c]">
                            <span className="text-xs font-medium text-[#808080] uppercase tracking-wide">
                                Schema Content
                            </span>
                            <button
                                onClick={handleCopy}
                                className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium transition-colors ${copied
                                    ? 'bg-green-900/30 text-green-400'
                                    : 'hover:bg-[#3c3c3c] text-[#808080]'
                                    }`}
                            >
                                {copied ? <Check size={14} /> : <Copy size={14} />}
                                {copied ? 'Copied!' : 'Copy'}
                            </button>
                        </div>
                        <pre
                            className="p-4 text-sm font-mono overflow-x-auto text-[#cccccc]"
                            dangerouslySetInnerHTML={{ __html: highlightJSON(contract.schema_content) }}
                        />
                    </div>
                </div>

                {/* Footer Actions */}
                <div className="flex-shrink-0 flex items-center justify-end gap-3 px-6 py-4 bg-[#1e1e1e]/50 border-t border-[#3c3c3c]">
                    <button
                        onClick={() => onExport(contract)}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
                    >
                        <Download size={16} />
                        Export
                    </button>
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-[#3c3c3c] hover:bg-[#4c4c4c] text-[#cccccc] font-medium rounded-lg transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ContractViewModal;
